// components/Layout.tsx
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Dialog,
    Paper,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import Draggable from 'react-draggable';
import {
    RootState, Ticket, ExtendedTicket, VendorSeverity, DetectionMethod,
    IntegrationAlert, IOC, IOCType, AccountStatus, PrivilegeLevel,
    AssetType, AssetCriticality, MitreMapping, AffectedIdentity,
    AffectedAsset, DuplicateAlert, DuplicateAlertGroup,
} from '../types';
import { clearSelectedTicket } from '../store/slices/ticketsSlice';
import { clearSelectedAlert } from '../store/slices/alertsSlice';
import { clearSelectedCorrelation } from '../store/slices/correlationsSlice';
import TicketDetails from './TicketDetails';
import AlertDetails from './AlertDetails';
import CorrelationDetails from './CorrelationDetails';
import {colors} from '../theme';
import { LayoutProvider } from './contexts/LayoutContext';
import InvestigationView from "./InvestigationView";

// Toggle this to enable multi-alert aggregation in TicketDetails
const AGGREGATE_ALERTS = true;

// Draggable Paper for the ticket floating panel.
// Cast to any: react-draggable 4.x types incorrectly require all DraggableProps.
const AnyDraggable = Draggable as any;

const makeDraggablePaper = (handle: string) =>
    React.forwardRef<HTMLDivElement, React.ComponentProps<typeof Paper>>(
        function DraggablePaperInner(props, _ref) {
            const nodeRef = React.useRef<HTMLDivElement>(null);
            return (
                <AnyDraggable
                    handle={handle}
                    cancel={'[class*="MuiDialogContent-root"], button, input, textarea, [role="slider"]'}
                    nodeRef={nodeRef}
                >
                    <Paper ref={nodeRef} {...props} />
                </AnyDraggable>
            );
        }
    );

const DraggablePaper = makeDraggablePaper('#ticket-dialog-drag-handle');
const DraggablePaperAlert = makeDraggablePaper('#alert-dialog-drag-handle');
const DraggablePaperCorrelation = makeDraggablePaper('#correlation-dialog-drag-handle');

// ============================================================================
// HELPERS: single-alert extraction
// ============================================================================

const buildIOCsFromAlert = (alert: IntegrationAlert): IOC[] => {
    const iocs: IOC[] = [];
    if (alert.file_hash) {
        iocs.push({
            id: `hash-${alert.id}`, type: IOCType.HASH_SHA256, value: alert.file_hash,
            confidence_score: 0, first_seen: alert.detected_at, last_seen: null,
            source: alert.integration_type, tags: [], is_malicious: null,
            reputation_score: null, related_campaigns: [],
        });
    }
    if (alert.ip_address) {
        iocs.push({
            id: `ip-${alert.id}`, type: IOCType.IP, value: alert.ip_address,
            confidence_score: 0, first_seen: alert.detected_at, last_seen: null,
            source: alert.integration_type, tags: [], is_malicious: null,
            reputation_score: null, related_campaigns: [],
        });
    }
    if (alert.file_name) {
        iocs.push({
            id: `file-${alert.id}`, type: IOCType.FILE_NAME, value: alert.file_name,
            confidence_score: 0, first_seen: alert.detected_at, last_seen: null,
            source: alert.integration_type, tags: [], is_malicious: null,
            reputation_score: null, related_campaigns: [],
        });
    }
    return iocs;
};

const buildIdentityFromAlert = (alert: IntegrationAlert): AffectedIdentity | null => {
    if (!alert.username) return null;
    return {
        username: alert.username,
        account_name: null, user_role: null, privilege_level: PrivilegeLevel.STANDARD,
        department: null, business_unit: null, email: null,
        account_status: AccountStatus.ACTIVE, last_login: null, risk_indicators: [],
    };
};

const buildAssetFromAlert = (alert: IntegrationAlert): AffectedAsset | null => {
    if (!alert.hostname) return null;
    return {
        hostname: alert.hostname, fqdn: null, ip_address: alert.ip_address || '',
        mac_address: null, asset_type: AssetType.ENDPOINT, os: null, os_version: null,
        platform: null, criticality: AssetCriticality.MEDIUM, owner: null,
        responsible_team: null, network_segment: null, vlan: null, location: null, tags: [],
    };
};

const buildMitreMappingsFromAlert = (alert: IntegrationAlert): MitreMapping[] => {
    if (!alert.mitre_tactic && !alert.mitre_technique) return [];
    const e = alert.mitre_enriched;
    return [{
        tactic:             e?.tactic_name     ?? alert.mitre_tactic    ?? '',
        tactic_id:          e?.tactic_id       ?? '',
        technique_id:       e?.technique_id    ?? alert.mitre_technique ?? '',
        technique_name:     e?.technique_name  ?? alert.mitre_technique ?? '',
        sub_technique_id:   e?.is_sub_technique ? (e.technique_id ?? null) : null,
        sub_technique_name: null,
        // Confidence is filled in by deduplicateMitreMappings based on cluster signal count
        confidence: 0,
        mitre_url: e?.url ?? (
            alert.mitre_technique?.match(/^T\d{4}(\.\d{3})?$/i)
                ? `https://attack.mitre.org/techniques/${alert.mitre_technique.replace('.', '/')}/`
                : null
        ),
    }];
};

// ============================================================================
// HELPERS: multi-alert aggregation
// ============================================================================

const SEVERITY_ORDER: Record<string, number> = {
    critical: 4, high: 3, medium: 2, low: 1, informational: 0,
};

const getHighestSeverity = (alerts: IntegrationAlert[]): VendorSeverity => {
    let highest = VendorSeverity.MEDIUM;
    let highestVal = -1;
    for (const a of alerts) {
        const val = SEVERITY_ORDER[a.severity || ''] ?? 0;
        if (val > highestVal) { highestVal = val; highest = (a.severity as VendorSeverity) || VendorSeverity.MEDIUM; }
    }
    return highest;
};

const getEarliestDetection = (alerts: IntegrationAlert[]): string => {
    return alerts
        .map(a => a.detected_at)
        .filter((d): d is string => d !== null)
        .sort()[0] || alerts[0]?.created_at || '';
};

const getTimeSpanMinutes = (alerts: IntegrationAlert[]): number => {
    const timestamps = alerts
        .map(a => a.detected_at || a.created_at)
        .filter(Boolean)
        .map(t => new Date(t).getTime())
        .sort((a, b) => a - b);
    if (timestamps.length < 2) return 0;
    return Math.round((timestamps[timestamps.length - 1] - timestamps[0]) / 60000);
};

const deduplicateIOCs = (alerts: IntegrationAlert[]): IOC[] => {
    const seen = new Set<string>();
    const iocs: IOC[] = [];
    for (const alert of alerts) {
        for (const ioc of buildIOCsFromAlert(alert)) {
            const key = `${ioc.type}:${ioc.value}`;
            if (!seen.has(key)) { seen.add(key); iocs.push(ioc); }
        }
    }
    return iocs;
};

const deduplicateMitreMappings = (alerts: IntegrationAlert[]): MitreMapping[] => {
    // Count how many alerts (and distinct sources) confirm each technique.
    const counts = new Map<string, { mapping: MitreMapping; sources: Set<string> }>();
    for (const alert of alerts) {
        for (const m of buildMitreMappingsFromAlert(alert)) {
            const key = `${m.tactic}:${m.technique_id}`;
            if (!counts.has(key)) {
                counts.set(key, { mapping: { ...m }, sources: new Set() });
            }
            counts.get(key)!.sources.add(alert.integration_type ?? 'unknown');
        }
    }
    // Derive confidence: 1 source = 50, 2 distinct sources = 75, 3+ = 90
    return Array.from(counts.values()).map(({ mapping, sources }) => ({
        ...mapping,
        confidence: sources.size >= 3 ? 90 : sources.size === 2 ? 75 : 50,
    }));
};

const mergeIdentities = (alerts: IntegrationAlert[]): AffectedIdentity | null => {
    for (const alert of alerts) {
        const identity = buildIdentityFromAlert(alert);
        if (identity) return identity;
    }
    return null;
};

const mergeAssets = (alerts: IntegrationAlert[]): AffectedAsset | null => {
    for (const alert of alerts) {
        const asset = buildAssetFromAlert(alert);
        if (asset) return asset;
    }
    return null;
};

const buildDuplicateAlertGroup = (alerts: IntegrationAlert[], primaryAlert: IntegrationAlert): DuplicateAlertGroup => {
    const toDuplicateAlert = (a: IntegrationAlert): DuplicateAlert => ({
        alert_id: a.id,
        alert_name: a.title,
        severity: (a.severity as VendorSeverity) || VendorSeverity.MEDIUM,
        timestamp: a.detected_at || a.created_at,
        source_system: a.integration_type || '',
        source_system_url: null,
        summary: a.description || null,
    });

    return {
        deduplication_id: primaryAlert.id,
        total_count: alerts.length,
        unique_platforms: Array.from(new Set(alerts.map(a => a.integration_type).filter(Boolean))),
        first_seen: getEarliestDetection(alerts),
        last_seen: alerts
            .map(a => a.detected_at || a.created_at)
            .filter(Boolean)
            .sort()
            .reverse()[0] || '',
        time_span_minutes: getTimeSpanMinutes(alerts),
        lead_alert: toDuplicateAlert(primaryAlert),
        duplicates: alerts.filter(a => a.id !== primaryAlert.id).map(toDuplicateAlert),
    };
};

// ============================================================================
// MAPPER: Ticket → ExtendedTicket
// ============================================================================

const toExtendedTicket = (ticket: Ticket): ExtendedTicket => {
    const alerts = ticket.integration_alerts?.slice().sort(
        (a, b) => (b.risk_score || 0) - (a.risk_score || 0)
    ) || [];
    const primaryAlert = alerts[0];

    // Base ticket (standard tier): only uses primary alert
    const base: ExtendedTicket = {
        id: ticket.id,
        title: ticket.title,
        description: ticket.description,
        meta: {
            id: ticket.id,
            created_at: ticket.created_at,
            updated_at: ticket.updated_at,
            status: ticket.status,
            assignee: ticket.assignee,
            assigned_team: null,
            priority: ticket.priority,
            sla_tier: null,
            sla_due_at: ticket.due_date,
            sla_remaining_minutes: null,
            source_system: primaryAlert?.integration_type || '',
            category: ticket.category,
        },
        alert: {
            alert_id: primaryAlert?.id || ticket.id,
            alert_name: primaryAlert?.title || ticket.title,
            rule_name: null,
            alert_type: ticket.category,
            vendor_severity: (primaryAlert?.severity as VendorSeverity) || VendorSeverity.MEDIUM,
            socmate_risk_score: primaryAlert?.risk_score || 0,
            detection_timestamp: primaryAlert?.detected_at || ticket.created_at,
            detection_method: DetectionMethod.SIGNATURE,
            description: primaryAlert?.description || ticket.description,
            risk_score_reason: null,
        },
        affected_identity: primaryAlert ? buildIdentityFromAlert(primaryAlert) : null,
        affected_asset: primaryAlert ? buildAssetFromAlert(primaryAlert) : null,
        network_context: null,
        mitre_mappings: primaryAlert ? buildMitreMappingsFromAlert(primaryAlert) : [],
        iocs: primaryAlert ? buildIOCsFromAlert(primaryAlert) : [],
        threat_intel: [],
        duplicate_alerts: null,
        timeline: [],
        impact_remediation: null,
        compliance: null,
        raw_data: primaryAlert?.raw_data || ticket.raw_data,
        comments_count: ticket.comments_count,
        attachments_count: ticket.attachments_count,
        client_recommendations: ticket.client_recommendations ?? null,
        resolution_summary: ticket.resolution_summary ?? null,
    };

    // Standard mode or single/no alert — return base as-is
    if (!AGGREGATE_ALERTS || alerts.length <= 1) {
        return base;
    }

    // Aggregated mode: merge data across all alerts
    return {
        ...base,
        alert: {
            ...base.alert,
            socmate_risk_score: Math.max(...alerts.map(a => a.risk_score || 0)),
            vendor_severity: getHighestSeverity(alerts),
            detection_timestamp: getEarliestDetection(alerts),
        },
        mitre_mappings: deduplicateMitreMappings(alerts),
        iocs: deduplicateIOCs(alerts),
        affected_identity: mergeIdentities(alerts),
        affected_asset: mergeAssets(alerts),
        duplicate_alerts: buildDuplicateAlertGroup(alerts, primaryAlert),
    };
};


// Drawer width constants
const DRAWER_WIDTH = 280;
const COLLAPSED_WIDTH = 64;
const TICKET_DETAILS_DEFAULT_WIDTH = 700;
const TICKET_DETAILS_MIN_WIDTH = 500;
const TICKET_DETAILS_MAX_WIDTH = 950;

interface LayoutProps {
    children: React.ReactNode;
    sidebarCollapsed?: boolean;
    isAdmin?: boolean; // Add this
    width?: string
}

const Layout: React.FC<LayoutProps> = ({ children, sidebarCollapsed, isAdmin, width }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { selectedTicket } = useSelector((state: RootState) => state.tickets);
    const { selectedAlert } = useSelector((state: RootState) => state.alerts);
    const { selectedCorrelation } = useSelector((state: RootState) => state.correlations);

    // State management

    // Resizable drawers
    const savedTicketWidth = localStorage.getItem('ticketDetailsWidth');
    const [ticketDetailsWidth, setTicketDetailsWidth] = useState<number>(
        savedTicketWidth ? Number(savedTicketWidth) : TICKET_DETAILS_DEFAULT_WIDTH
    );

    const resizingDrawer = useRef<'ticket' | null>(null);

    const handleTicketResizeMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        resizingDrawer.current = 'ticket';
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, []);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!resizingDrawer.current) return;
            const newWidth = window.innerWidth - e.clientX;
            if (resizingDrawer.current === 'ticket') {
                setTicketDetailsWidth(Math.min(TICKET_DETAILS_MAX_WIDTH, Math.max(TICKET_DETAILS_MIN_WIDTH, newWidth)));
            }
        };

        const handleMouseUp = () => {
            if (!resizingDrawer.current) return;
            if (resizingDrawer.current === 'ticket') {
                localStorage.setItem('ticketDetailsWidth', String(ticketDetailsWidth));
            }
            resizingDrawer.current = null;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [ticketDetailsWidth]);

    const handleCloseInvestigation = () => {
        dispatch(clearSelectedTicket());
    };

    // Calculate current sidebar width
    const currentSidebarWidth = sidebarCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;
    
    // Layout context value
    const layoutContextValue = {
        sidebarCollapsed: sidebarCollapsed ?? false,
        isMobile,
        sidebarWidth: currentSidebarWidth,
        ticketDetailsOpen: !!selectedTicket,
        ticketDetailsWidth: ticketDetailsWidth,
    };

    return (
            <Box className="main-container" sx={{
                display: 'flex',
                minHeight: '100vh',
                backgroundColor: theme.palette.background.default, 
                width: width || '100%'
            }}>



            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    backgroundColor: theme.palette.background.default,
                    minHeight: '100vh',
                    overflowX: 'hidden',
                  }}
            >
                <LayoutProvider value={layoutContextValue}>
                    {children}
                </LayoutProvider>
            </Box>

            {/* Ticket details — draggable floating panel */}
            <Dialog
                open={!!selectedTicket}
                onClose={handleCloseInvestigation}
                hideBackdrop
                disableEnforceFocus
                disableScrollLock
                PaperComponent={DraggablePaper}
                PaperProps={{
                    sx: {
                        width: '62vw',
                        maxWidth: '900px',
                        minWidth: '520px',
                        height: '93vh',
                        maxHeight: '93vh',
                        m: 0,
                        position: 'fixed',
                        top: '2vh',
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.border.light}`,
                        borderRadius: '12px',
                        boxShadow: '0 0 0 1px oklch(100% 0 0 / 6%), 0 8px 32px -4px oklch(0% 0 0 / 40%), 0 32px 80px -8px oklch(0% 0 0 / 60%)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        pointerEvents: 'auto',
                    },
                }}
                sx={{ pointerEvents: 'none', '& .MuiDialog-container': { pointerEvents: 'none' } }}
            >
                {/* Drag grip — only this strip initiates dragging */}
                <Box
                    id="ticket-dialog-drag-handle"
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 18,
                        flexShrink: 0,
                        cursor: 'move',
                        backgroundColor: theme.palette.background.paper,
                        borderBottom: `1px solid ${theme.palette.border.light}`,
                        pointerEvents: 'auto',
                    }}
                >
                    <Box sx={{
                        width: 32,
                        height: 3,
                        borderRadius: 2,
                        backgroundColor: theme.palette.text.disabled,
                        opacity: 0.5,
                    }} />
                </Box>
                <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', pointerEvents: 'auto' }}>
                    {selectedTicket && (
                        <TicketDetails
                            ticket={toExtendedTicket(selectedTicket)}
                            integrationAlerts={selectedTicket.integration_alerts || []}
                        />
                    )}
                </Box>
            </Dialog>

            {/* Alert details — draggable floating panel */}
            <Dialog
                open={!!selectedAlert}
                onClose={() => dispatch(clearSelectedAlert())}
                hideBackdrop
                disableEnforceFocus
                disableScrollLock
                PaperComponent={DraggablePaperAlert}
                PaperProps={{
                    sx: {
                        width: '62vw',
                        maxWidth: '900px',
                        minWidth: '520px',
                        height: '93vh',
                        maxHeight: '93vh',
                        m: 0,
                        position: 'fixed',
                        top: '2vh',
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.border.light}`,
                        borderRadius: '12px',
                        boxShadow: '0 0 0 1px oklch(100% 0 0 / 6%), 0 8px 32px -4px oklch(0% 0 0 / 40%), 0 32px 80px -8px oklch(0% 0 0 / 60%)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        pointerEvents: 'auto',
                    },
                }}
                sx={{ pointerEvents: 'none', '& .MuiDialog-container': { pointerEvents: 'none' } }}
            >
                {/* Drag grip */}
                <Box
                    id="alert-dialog-drag-handle"
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 18,
                        flexShrink: 0,
                        cursor: 'move',
                        backgroundColor: theme.palette.background.paper,
                        borderBottom: `1px solid ${theme.palette.border.light}`,
                        pointerEvents: 'auto',
                    }}
                >
                    <Box sx={{
                        width: 32,
                        height: 3,
                        borderRadius: 2,
                        backgroundColor: theme.palette.text.disabled,
                        opacity: 0.5,
                    }} />
                </Box>
                <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', pointerEvents: 'auto' }}>
                    {selectedAlert && (
                        <AlertDetails alert={selectedAlert} />
                    )}
                </Box>
            </Dialog>

            {/* Correlation details — draggable floating panel */}
            <Dialog
                open={!!selectedCorrelation}
                onClose={() => dispatch(clearSelectedCorrelation())}
                hideBackdrop
                disableEnforceFocus
                disableScrollLock
                PaperComponent={DraggablePaperCorrelation}
                PaperProps={{
                    sx: {
                        width: '62vw',
                        maxWidth: '900px',
                        minWidth: '520px',
                        height: '93vh',
                        maxHeight: '93vh',
                        m: 0,
                        position: 'fixed',
                        top: '2vh',
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.border.light}`,
                        borderRadius: '12px',
                        boxShadow: '0 0 0 1px oklch(100% 0 0 / 6%), 0 8px 32px -4px oklch(0% 0 0 / 40%), 0 32px 80px -8px oklch(0% 0 0 / 60%)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        pointerEvents: 'auto',
                    },
                }}
                sx={{ pointerEvents: 'none', '& .MuiDialog-container': { pointerEvents: 'none' } }}
            >
                {/* Drag grip */}
                <Box
                    id="correlation-dialog-drag-handle"
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 18,
                        flexShrink: 0,
                        cursor: 'move',
                        backgroundColor: theme.palette.background.paper,
                        borderBottom: `1px solid ${theme.palette.border.light}`,
                        pointerEvents: 'auto',
                    }}
                >
                    <Box sx={{
                        width: 32,
                        height: 3,
                        borderRadius: 2,
                        backgroundColor: theme.palette.text.disabled,
                        opacity: 0.5,
                    }} />
                </Box>
                <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', pointerEvents: 'auto' }}>
                    {selectedCorrelation && (
                        <CorrelationDetails correlation={selectedCorrelation} />
                    )}
                </Box>
            </Dialog>
        </Box>
    );
};

export default Layout;